/**
 * Inline stroke icons for the shell (ARCHITECTURE.md §8).
 *
 * Hand-drawn 24px stroke glyphs — no icon dependency, no runtime font. Each
 * inherits `currentColor` and a 1.6 stroke so they read as one set at nav scale.
 */
import type { ReactNode, SVGProps } from "react";

export type IconProps = SVGProps<SVGSVGElement>;

function Svg({ children, ...props }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
}

export function DashboardIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M4 13h6V4H4zM14 20h6v-9h-6zM4 20h6v-4H4zM14 8h6V4h-6z" />
    </Svg>
  );
}

export function PortfolioIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M4 19V5m0 14h16M4 19l4-5 3 3 4-6 5 4" />
    </Svg>
  );
}

export function MarketIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M8 4v3m0 7v3" />
      <rect x="6" y="7" width="4" height="7" rx="0.5" />
      <path d="M16 7v3m0 7v3" />
      <rect x="14" y="10" width="4" height="7" rx="0.5" />
    </Svg>
  );
}

export function StrategiesIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="6" cy="6" r="2.2" />
      <circle cx="18" cy="12" r="2.2" />
      <circle cx="6" cy="18" r="2.2" />
      <path d="M8 7l8 4M8 17l8-4" />
    </Svg>
  );
}

export function ChatIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M4 5h16v11H9l-5 3z" />
      <path d="M8 9h8M8 12h5" />
    </Svg>
  );
}

export function OrdersIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M6 3h9l3 3v15H6z" />
      <path d="M9 9h6M9 13h6M9 17h4" />
    </Svg>
  );
}

export function ApprovalsIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M4 12l2.5-2.5M4 12l2.5 2.5M4 12h9" />
      <path d="M13 4h5a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-5" />
    </Svg>
  );
}

export function NotificationsIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </Svg>
  );
}

export function SettingsIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9L17 7M7 17l-2.1 2.1" />
    </Svg>
  );
}

export function LockIcon(p: IconProps) {
  return (
    <Svg {...p} width="13" height="13">
      <rect x="5" y="11" width="14" height="9" rx="1.5" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </Svg>
  );
}

export function SunIcon(p: IconProps) {
  return (
    <Svg {...p} width="16" height="16">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
    </Svg>
  );
}

export function MoonIcon(p: IconProps) {
  return (
    <Svg {...p} width="16" height="16">
      <path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" />
    </Svg>
  );
}

export function LogoutIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M14 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4" />
      <path d="M10 12H3m0 0l3-3m-3 3l3 3" />
    </Svg>
  );
}
